import React, { FunctionComponent, ReactNode, useEffect, useRef } from 'react';
import { Button } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import styles from './Callout.module.scss';

export type CalloutTone = 'warning' | 'info' | 'error' | 'success' | 'neutral';

export interface CalloutProps {
  tone: CalloutTone;
  title?: ReactNode;
  children: ReactNode;
  /** Gdy podane, komunikat da się zamknąć; `closeLabel` to nazwa przycisku dla czytnika ekranu. */
  onClose?: () => void;
  closeLabel?: string;
  /**
   * Domyślnie `alert` dla błędu, `status` dla reszty. Komunikat, który pojawia się po akcji
   * użytkownika (wynik wysłania), wymaga `alert`, żeby czytnik ekranu ogłosił go od razu.
   */
  role?: 'alert' | 'status';
  /**
   * Komunikat będący wynikiem akcji przenosi na siebie fokus przy pojawieniu się (czytnik ekranu
   * ogłasza go, a klawiatura zostaje przy wyniku). Fokus programowy, bez obwódki.
   */
  focusOnMount?: boolean;
}

const ICONS: Record<CalloutTone, ReactNode> = {
  warning: <ExclamationCircleOutlined aria-hidden />,
  info: <InfoCircleOutlined aria-hidden />,
  error: <CloseCircleOutlined aria-hidden />,
  success: <CheckCircleOutlined aria-hidden />,
  neutral: <InfoCircleOutlined aria-hidden />,
};

const Callout: FunctionComponent<CalloutProps> = ({
  tone,
  title,
  children,
  onClose,
  closeLabel,
  role = tone === 'error' ? 'alert' : 'status',
  focusOnMount = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusOnMount) return;
    ref.current?.scrollIntoView?.({ block: 'nearest' });
    ref.current?.focus();
  }, [focusOnMount]);

  return (
    <div
      ref={ref}
      className={`${styles.callout} ${styles[tone]}`}
      role={role}
      tabIndex={focusOnMount ? -1 : undefined}
    >
      <span className={styles.icon}>{ICONS[tone]}</span>
      <div className={styles.body}>
        {title && <strong className={styles.title}>{title}</strong>}
        {children}
      </div>
      {onClose && (
        <Button
          type="text"
          size="small"
          className={styles.close}
          icon={<CloseOutlined aria-hidden />}
          aria-label={closeLabel}
          onClick={onClose}
        />
      )}
    </div>
  );
};

export default Callout;
